import React from 'react';
import { Box } from '@mui/material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

export interface MarkdownProps {
  children: string;
  /**
   * `body` — reading text, for answers.
   * `compact` — smaller secondary text, for notes and step descriptions.
   */
  variant?: 'body' | 'compact';
}

/**
 * Lines the model starts with a typed bullet ("• Scope: all") become list
 * items. Markdown only knows "-", "*" and "+", so without this they run into
 * the line before. Code blocks are left exactly as written.
 */
export function typedBulletsToList(text: string): string {
  let inCode = false;
  return text
    .split('\n')
    .map((line) => {
      if (/^\s*(```|~~~)/.test(line)) {
        inCode = !inCode;
        return line;
      }
      return inCode ? line : line.replace(/^(\s*)[•●▪◦‣]\s+/, '$1- ');
    })
    .join('\n');
}

/**
 * Markdown written by the model — paragraphs, headings, lists, bold, links,
 * quotes, code and tables — set in the app's type and colours.
 *
 * Not for text a person typed: show that as plain text, so nothing they write
 * is reinterpreted as formatting.
 */
export function Markdown({ children, variant = 'body' }: MarkdownProps) {
  const compact = variant === 'compact';
  return (
    <Box
      sx={(t) => ({
        ...(compact ? t.typography.caption : t.typography.body2),
        color: compact ? t.tokens.text.secondary : t.tokens.text.primary,
        minWidth: 0,
        overflowWrap: 'anywhere',
        // Browser margins off — at no weight, so the gaps below always win.
        '& :where(p, ul, ol, pre, table, blockquote, h1, h2, h3, h4, h5, h6)': { m: 0 },
        // One gap between blocks, a little more before a heading.
        '& > * + *': { mt: compact ? 0.5 : 1.5 },
        '& > * + h1, & > * + h2, & > * + h3, & > * + h4': { mt: compact ? 1 : 2.5 },
        '& h1, & h2, & h3': { ...(compact ? t.typography.subtitle2 : t.typography.subtitle1), color: t.tokens.text.primary },
        '& h4, & h5, & h6': { ...t.typography.subtitle2, color: t.tokens.text.primary },
        '& ul, & ol': { pl: 2.5 },
        '& li > ul, & li > ol': { mt: 0.25 },
        '& blockquote': {
          pl: 1.5,
          borderLeft: `2px solid ${t.tokens.border.default}`,
          color: t.tokens.text.secondary,
        },
        '& hr': { border: 0, borderTop: `1px solid ${t.tokens.border.subtle}`, my: compact ? 1 : 2 },
        // Bullets that run to two lines need air between them to read as separate.
        '& li + li': { mt: compact ? 0.25 : 0.75 },
        '& strong': { fontWeight: 600, color: t.tokens.text.primary },
        '& a': { color: t.tokens.text.accent },
        '& code': {
          fontFamily: t.typography.fontFamilyMono,
          bgcolor: t.tokens.chat.codeBg,
          px: 0.5,
          borderRadius: `${t.ds.radius.xs}px`,
        },
        '& pre': {
          fontFamily: t.typography.fontFamilyMono,
          bgcolor: t.tokens.chat.codeBg,
          p: 1.5,
          borderRadius: `${t.ds.radius.sm}px`,
          overflowX: 'auto',
          '& code': { bgcolor: 'transparent', px: 0 },
        },
        '& table': {
          ...t.typography.caption,
          display: 'block',
          overflowX: 'auto',
          borderCollapse: 'collapse',
        },
        '& th, & td': {
          border: `1px solid ${t.tokens.border.subtle}`,
          px: 1,
          py: 0.5,
          textAlign: 'left',
          fontVariantNumeric: 'tabular-nums',
        },
        '& th': { bgcolor: t.tokens.surface.sunken, fontWeight: 600 },
      })}
    >
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{typedBulletsToList(children)}</ReactMarkdown>
    </Box>
  );
}
